import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Agent } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';

interface ZAPIConfig {
  clientToken: string;
  instanceId: string;
  instanceToken: string;
}

interface ZAPIConfigModalProps {
  open: boolean;
  onClose: () => void;
  agent: Agent | null;
  onSave: (config: ZAPIConfig) => void;
  isLoading: boolean;
}

const ZAPIConfigModal: React.FC<ZAPIConfigModalProps> = ({
  open,
  onClose,
  agent,
  onSave,
  isLoading
}) => {
  const theme = useTheme();
  const [config, setConfig] = useState<ZAPIConfig>({
    clientToken: '',
    instanceId: '',
    instanceToken: ''
  });
  const [showTokens, setShowTokens] = useState({
    clientToken: false,
    instanceToken: false
  });

  useEffect(() => {
    if (agent?.zApiConfig) {
      setConfig({
        clientToken: agent.zApiConfig.clientToken || '',
        instanceId: agent.zApiConfig.instanceId || '',
        instanceToken: agent.zApiConfig.instanceToken || ''
      });
    } else {
      setConfig({
        clientToken: '',
        instanceId: '',
        instanceToken: ''
      });
    }
  }, [agent, open]);

  const handleSave = () => {
    if (!config.clientToken || !config.instanceId || !config.instanceToken) {
      return;
    }
    onSave(config);
  };

  const isFormValid = config.clientToken && config.instanceId && config.instanceToken;

  const toggleTokenVisibility = (field: 'clientToken' | 'instanceToken') => {
    setShowTokens(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Configurar Z-API
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Configure suas credenciais da Z-API para conectar seu WhatsApp.
          </Typography>

          <TextField
            label="Client Token"
            value={config.clientToken}
            onChange={(e) => setConfig(prev => ({ ...prev, clientToken: e.target.value }))}
            fullWidth
            type={showTokens.clientToken ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggleTokenVisibility('clientToken')}
                    edge="end"
                  >
                    {showTokens.clientToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            helperText="Token de cliente fornecido pela Z-API"
          />

          <TextField
            label="Instance ID"
            value={config.instanceId}
            onChange={(e) => setConfig(prev => ({ ...prev, instanceId: e.target.value }))}
            fullWidth
            helperText="ID da instância da Z-API"
          />

          <TextField
            label="Instance Token"
            value={config.instanceToken}
            onChange={(e) => setConfig(prev => ({ ...prev, instanceToken: e.target.value }))}
            fullWidth
            type={showTokens.instanceToken ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggleTokenVisibility('instanceToken')}
                    edge="end"
                  >
                    {showTokens.instanceToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            helperText="Token da instância fornecido pela Z-API"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZAPIConfigModal;
